import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Project } from '../types/project';
import { projectService } from '../services/projectService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

const ProjectDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImage, setActiveImage] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadProject(id);
    }
  }, [id]);

  const loadProject = async (projectId: string) => {
    try {
      setIsLoading(true);
      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);
      
      if (projectSnap.exists()) {
        const projectData = projectSnap.data() as Omit<Project, 'id'>;
        setProject({
          id: projectId,
          ...projectData
        });
        
        if (projectData.images && projectData.images.length > 0) {
          setActiveImage(projectData.images[0]);
        }
      } else {
        setError('Project not found');
      }
    } catch (err) {
      setError('Failed to load project details');
      console.error('Error loading project:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white">
        <div className="container mx-auto px-4 py-16">
          <div className="flex items-center justify-center h-64">
            <div className="text-primary-500">Loading project details...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="bg-white">
        <div className="container mx-auto px-4 py-16">
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error || 'Project not found'}</div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <Link to="/projects" className="text-accent-600 hover:text-accent-700">
              &larr; Back to Projects
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="container mx-auto px-4 py-16">
        <div className="mb-6">
          <Link to="/projects" className="text-accent-600 hover:text-accent-700 inline-flex items-center">
            <svg className="mr-2 w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back to Projects
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            {/* Main Image */}
            {activeImage ? (
              <div className="rounded-lg overflow-hidden shadow-lg mb-4">
                <img 
                  src={activeImage} 
                  alt={project.title} 
                  className="w-full h-96 object-cover"
                />
              </div>
            ) : (
              <div className="rounded-lg overflow-hidden shadow-lg mb-4 bg-primary-100 h-96 flex items-center justify-center">
                <span className="text-primary-500">No Image Available</span>
              </div>
            )}

            {/* Thumbnails */}
            {project.images && project.images.length > 1 && (
              <div className="grid grid-cols-5 gap-2">
                {project.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImage(image)}
                    className={`rounded-md overflow-hidden ${activeImage === image ? 'ring-2 ring-accent-500' : ''}`}
                  >
                    <img 
                      src={image} 
                      alt={`${project.title} thumbnail ${index + 1}`} 
                      className="w-full h-20 object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-display font-bold text-primary-900">
                {project.title}
              </h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                project.status === 'Completed' 
                  ? 'bg-green-100 text-green-800' 
                  : project.status === 'In Progress' 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {project.status}
              </span>
            </div>

            <div className="border-t border-b border-primary-200 py-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-primary-500">Location</p>
                  <p className="font-medium">{project.location}</p>
                </div>
                <div>
                  <p className="text-sm text-primary-500">Square Feet</p>
                  <p className="font-medium">{project.squareFeet}</p>
                </div>
                <div>
                  <p className="text-sm text-primary-500">Completion Date</p>
                  <p className="font-medium">{project.completionDate}</p>
                </div>
                {project.featured && (
                  <div>
                    <p className="text-sm text-primary-500">Featured</p>
                    <p className="font-medium flex items-center">
                      <span className="inline-block h-2 w-2 rounded-full bg-green-500 mr-2"></span>
                      Yes
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="prose max-w-none">
              <h2 className="text-xl font-semibold text-primary-900 mb-4">Description</h2>
              <p className="text-primary-700 whitespace-pre-line">
                {project.description || 'No description available.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail; 